import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { SHIPPING_ORIGINS } from '../../lib/shippingOrigins';

const cloneOrigins = (origins) => origins.map((origin) => ({ ...origin }));
const BASE_ORIGIN_CODES = new Set(SHIPPING_ORIGINS.map((origin) => origin.code));

const DEFAULT_SETTINGS = {
  store_name: 'Hunar',
  store_tagline: 'Crafted with Love by Friends & Family',
  site_discount_pct: '0',
  currency_symbol: '₹',
  announcement_enabled: 'true',
  announcement_text: '',
  contact_email: 'lakshaychawla13@gmail.com',
  shipping_origins: cloneOrigins(SHIPPING_ORIGINS),
};

export default function AdminSettingsPage() {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [originLookupStates, setOriginLookupStates] = useState({});

  useEffect(() => {
    loadSettings();
  }, [token]);

  const loadSettings = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await fetch('/api/settings');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to load store settings.');
      let origins = DEFAULT_SETTINGS.shipping_origins;
      if (data.shipping_origins) {
        try {
          const parsed = typeof data.shipping_origins === 'string' ? JSON.parse(data.shipping_origins) : data.shipping_origins;
          if (Array.isArray(parsed) && parsed.length > 0) origins = parsed;
        } catch (_error) {
          origins = DEFAULT_SETTINGS.shipping_origins;
        }
      }
      setSettings((current) => ({ ...DEFAULT_SETTINGS, ...current, ...data, shipping_origins: origins }));
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMessage('');
    try {
      const payload = {
        ...settings,
        shipping_origins: JSON.stringify(settings.shipping_origins || DEFAULT_SETTINGS.shipping_origins),
      };
      const res = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to update store settings.');

      let savedOrigins = settings.shipping_origins;
      if (data.settings?.shipping_origins) {
        try {
          savedOrigins = typeof data.settings.shipping_origins === 'string'
            ? JSON.parse(data.settings.shipping_origins)
            : data.settings.shipping_origins;
        } catch (_error) {}
      }
      setSettings((current) => ({ ...current, ...(data.settings || {}), shipping_origins: savedOrigins }));
      setToastMessage('Store settings updated successfully! 🎉');
      setTimeout(() => setToastMessage(''), 3000);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateOrigin = (index, changes) => {
    setSettings((current) => ({
      ...current,
      shipping_origins: current.shipping_origins.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...changes } : item
      )),
    }));
  };

  const detectOriginArea = async (index) => {
    const origin = settings.shipping_origins?.[index];
    const pincode = String(origin?.pickupPincode || '').replace(/\D/g, '').slice(0, 6);
    if (!/^\d{6}$/.test(pincode)) {
      setOriginLookupStates((current) => ({
        ...current,
        [origin?.code]: { type: 'error', message: 'Enter a valid 6-digit PIN first.' },
      }));
      return;
    }

    setOriginLookupStates((current) => ({
      ...current,
      [origin.code]: { type: 'loading', message: 'Detecting area…' },
    }));
    try {
      const response = await fetch(`/api/addresses/pincode/${pincode}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'PIN area could not be detected.');
      updateOrigin(index, { pickupPincode: pincode, city: data.city, state: data.state });
      setOriginLookupStates((current) => ({
        ...current,
        [origin.code]: { type: 'success', message: `Detected ${data.city}, ${data.state}.` },
      }));
    } catch (error) {
      setOriginLookupStates((current) => ({
        ...current,
        [origin.code]: { type: 'error', message: error.message || 'PIN area could not be detected.' },
      }));
    }
  };

  const handleAddOrigin = () => {
    const currentOrigins = settings.shipping_origins || [];
    const existingCodes = new Set(currentOrigins.map((origin) => origin.code));
    let suffix = currentOrigins.length + 1;
    let code = `LOCATION_${suffix}`;
    while (existingCodes.has(code)) {
      suffix += 1;
      code = `LOCATION_${suffix}`;
    }
    setSettings((current) => ({
      ...current,
      shipping_origins: [
        ...(current.shipping_origins || []),
        {
          code,
          label: '',
          address: '',
          pickupPincode: '',
          city: '',
          state: '',
          region: 'north',
          nearbyPincodePrefixes: [],
          active: true,
          sortOrder: currentOrigins.length + 1,
        },
      ],
    }));
  };

  const handleRemoveOrigin = (index) => {
    setSettings((current) => ({
      ...current,
      shipping_origins: current.shipping_origins.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="brand-font mb-4">Store Settings & Site-wide Discount</h2>

      {toastMessage && (
        <div className="alert alert-success py-2 px-3 small text-center mb-3 fade show shadow-sm">
          {toastMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="spinner-border text-primary" role="status"></div>
      ) : (
        <form onSubmit={handleSaveSettings} className="p-4 border rounded bg-card shadow-sm" style={{ maxWidth: '700px' }}>
          <h5 className="font-weight-bold mb-3">Global Store Configuration</h5>

          <div className="mb-3">
            <label className="form-label small font-weight-bold">Site-wide Discount % (Super Admin Setting)</label>
            <input
              type="number"
              className="form-control"
              min="0"
              max="100"
              value={settings.site_discount_pct || 0}
              onChange={(e) => setSettings({ ...settings, site_discount_pct: e.target.value })}
            />
            <span className="text-muted small">Applies sequentially across all products in the store</span>
          </div>

          <div className="mb-3">
            <label className="form-label small font-weight-bold">Store Name</label>
            <input
              type="text"
              className="form-control"
              value={settings.store_name || ''}
              onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label small font-weight-bold">Store Tagline</label>
            <input
              type="text"
              className="form-control"
              value={settings.store_tagline || ''}
              onChange={(e) => setSettings({ ...settings, store_tagline: e.target.value })}
            />
          </div>

          <div className="mb-3">
            <label className="form-label small font-weight-bold">Announcement Banner Text</label>
            <textarea
              className="form-control"
              rows="2"
              value={settings.announcement_text || ''}
              onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })}
            ></textarea>
          </div>

          <div className="form-check mb-4">
            <input
              id="announcement-enabled"
              type="checkbox"
              className="form-check-input"
              checked={settings.announcement_enabled === 'true'}
              onChange={(e) => setSettings({ ...settings, announcement_enabled: String(e.target.checked) })}
            />
            <label className="form-check-label small" htmlFor="announcement-enabled">Show announcement banner</label>
          </div>

          <div className="mb-4">
            <label className="form-label small font-weight-bold">Contact Email</label>
            <input
              type="email"
              className="form-control"
              value={settings.contact_email || ''}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
            />
          </div>

          <section className="mb-4" aria-labelledby="shipping-origins-heading">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
              <div>
                <h5 id="shipping-origins-heading" className="font-weight-bold mb-1">Delivery source locations</h5>
                <p className="text-muted small mb-0">Keep the original three sources, or add more studios and pickup points below.</p>
              </div>
              <button type="button" className="btn btn-outline-custom btn-sm rounded-pill" onClick={handleAddOrigin}>+ Add location</button>
            </div>
            <p className="text-muted small mb-3">These names appear on products and are used for carrier rates. A full pickup address is required before customers can see or choose collection.</p>
            {(settings.shipping_origins || DEFAULT_SETTINGS.shipping_origins).map((origin, index) => (
              <div key={origin.code} className="border rounded p-3 mb-3 bg-light-subtle">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>{origin.code}</strong>
                  <label className="form-check form-switch small mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={origin.active !== false}
                      onChange={(e) => updateOrigin(index, { active: e.target.checked })}
                    />
                    <span className="form-check-label">Active</span>
                  </label>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label small" htmlFor={`shipping-origin-label-${origin.code}`}>Display name</label>
                    <input
                      id={`shipping-origin-label-${origin.code}`}
                      className="form-control form-control-sm"
                      value={origin.label || ''}
                      onChange={(e) => updateOrigin(index, { label: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small" htmlFor={`shipping-origin-pincode-${origin.code}`}>Pickup pincode</label>
                    <div className="input-group input-group-sm">
                      <input
                        id={`shipping-origin-pincode-${origin.code}`}
                        className="form-control"
                        inputMode="numeric"
                        maxLength="6"
                        value={origin.pickupPincode || ''}
                        onChange={(e) => {
                          updateOrigin(index, { pickupPincode: e.target.value.replace(/\D/g, '').slice(0, 6) });
                          setOriginLookupStates((current) => ({ ...current, [origin.code]: null }));
                        }}
                      />
                      <button type="button" className="btn btn-outline-custom" onClick={() => detectOriginArea(index)} disabled={originLookupStates[origin.code]?.type === 'loading'}>
                        {originLookupStates[origin.code]?.type === 'loading' ? 'Detecting…' : 'Detect area'}
                      </button>
                    </div>
                    {originLookupStates[origin.code]?.message && (
                      <span className={`text-${originLookupStates[origin.code].type === 'error' ? 'danger' : originLookupStates[origin.code].type === 'success' ? 'success' : 'muted'} small`} role="status">
                        {originLookupStates[origin.code].message}
                      </span>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small" htmlFor={`shipping-origin-city-${origin.code}`}>Pickup city</label>
                    <input
                      id={`shipping-origin-city-${origin.code}`}
                      className="form-control form-control-sm"
                      value={origin.city || ''}
                      onChange={(e) => updateOrigin(index, { city: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small" htmlFor={`shipping-origin-state-${origin.code}`}>Pickup state</label>
                    <input
                      id={`shipping-origin-state-${origin.code}`}
                      className="form-control form-control-sm"
                      value={origin.state || ''}
                      onChange={(e) => updateOrigin(index, { state: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small" htmlFor={`shipping-origin-address-${origin.code}`}>Full pickup address</label>
                    <textarea
                      id={`shipping-origin-address-${origin.code}`}
                      className="form-control form-control-sm"
                      rows="2"
                      placeholder="House or studio, street, locality"
                      value={origin.address || ''}
                      onChange={(e) => updateOrigin(index, { address: e.target.value })}
                    />
                    <span className="text-muted small">Shown to customers only when collection is available for their PIN area.</span>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small" htmlFor={`shipping-origin-region-${origin.code}`}>Rate region</label>
                    <input
                      id={`shipping-origin-region-${origin.code}`}
                      className="form-control form-control-sm"
                      value={origin.region || ''}
                      onChange={(e) => updateOrigin(index, { region: e.target.value.toLowerCase().replace(/[^a-z]/g, '').slice(0, 20) })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small" htmlFor={`shipping-origin-nearby-pincodes-${origin.code}`}>Nearby pickup PIN prefixes</label>
                    <input
                      id={`shipping-origin-nearby-pincodes-${origin.code}`}
                      className="form-control form-control-sm"
                      inputMode="numeric"
                      placeholder="121, 122, 201"
                      value={Array.isArray(origin.nearbyPincodePrefixes) ? origin.nearbyPincodePrefixes.join(', ') : (origin.nearbyPincodePrefixes || '')}
                      onChange={(e) => updateOrigin(index, { nearbyPincodePrefixes: e.target.value })}
                    />
                    <span className="text-muted small">Optional first-three-digit PIN prefixes where customers may collect from this location.</span>
                  </div>
                </div>
                {!BASE_ORIGIN_CODES.has(origin.code) && (
                  <button type="button" className="btn btn-link btn-sm text-danger px-0 mt-2" onClick={() => handleRemoveOrigin(index)}>
                    Remove this added location
                  </button>
                )}
              </div>
            ))}
          </section>

          <button type="submit" className="btn btn-primary-custom rounded-pill px-4" disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Settings'}
          </button>
        </form>
      )}
    </div>
  );
}
